import axios from "axios";
import { notifyError, notifySuccess } from "@/components/prospect/prospect.util";
import { PALETTE } from "./trip-constants";

// Edicao do roteiro: incluir/tirar casos, passar entre equipes, arrastar ordem e dias, travar dia, combinar dia/horario e equipes.
// Mixin do ProspectTripEditor: usa o estado dele (trip, form, teams, stops...).
export default {
    methods: {
        // Casos
        addCases(ids) {
            const team = this.activeTeam;
            if (!team) return;
            const fresh = ids.filter((id) => !this.stopByCase.has(id) && this.caseIndex.get(id) && this.caseIndex.get(id).municipality);
            if (!fresh.length) return;
            fresh.forEach((id) => this.stops.push({ case_id: id, team_key: team.key, position: null, pin_day: null, pin_time: null, locked: false }));
            this.rebuildLists();
            this.queueSave();
        },

        removeCases(ids) {
            this.stops = this.stops.filter((stop) => !ids.includes(stop.case_id));
            this.rebuildLists();
            this.queueSave();
        },

        releasePins(stop) {
            if (stop.locked) {
                stop.pin_day = null;
                stop.pin_time = null;
                stop.locked = false;
            }
        },

        moveToTeam(caseId, key) {
            const stop = this.stopOf(caseId);
            if (!stop || stop.team_key === key) return;
            stop.team_key = key;
            stop.position = null;
            this.releasePins(stop);
            this.rebuildLists();
            this.queueSave();
        },

        // Arrastar: ordem (entre equipes) e dias
        scheduleApply(kind, key) {
            // Mover entre listas dispara change nas duas; aplica uma vez.
            if (this.applyPending[kind]) return;
            this.applyPending[kind] = true;
            this.$nextTick(() => {
                this.applyPending[kind] = false;
                if (kind === "order") this.applyOrder();
                else this.applyDays(key);
            });
        },

        applyOrder() {
            this.teams.forEach((team) => {
                const list = this.orderLists[team.key] || [];
                const before = this.stops.filter((stop) => stop.team_key === team.key);
                const changed = list.length !== before.length || list.some((caseId, index) => {
                    const stop = this.stopOf(caseId);
                    return !stop || stop.team_key !== team.key || (team.manual_order && stop.position !== index);
                });
                const plan = this.planOf(team);
                const reordered = !team.manual_order && plan && list.join(",") !== plan.case_order.filter((id) => list.includes(id)).join(",");
                if (!changed && !reordered) return;
                team.manual_order = true;
                list.forEach((caseId, index) => {
                    const stop = this.stopOf(caseId);
                    if (!stop) return;
                    if (stop.team_key !== team.key) {
                        stop.team_key = team.key;
                        this.releasePins(stop);
                    }
                    stop.position = index;
                });
            });
            this.queueSave();
        },

        applyDays(key) {
            const team = this.teams.find((item) => item.key === key);
            const lists = this.dayLists[key] || [];
            if (!team) return;
            lists.forEach((list, dayIndex) => {
                list.forEach((caseId) => {
                    const visit = this.visitIndex.get(caseId);
                    const stop = this.stopOf(caseId);
                    if (stop && (!visit || visit.day !== dayIndex + 1)) {
                        stop.pin_day = dayIndex + 1;
                        stop.pin_time = null;
                        stop.locked = false;
                    }
                });
            });
            team.manual_order = true;
            lists.flat().forEach((caseId, index) => {
                const stop = this.stopOf(caseId);
                if (stop) stop.position = index;
            });
            this.queueSave();
        },

        isLocked(team, day) {
            return team.locked_days.includes(day);
        },

        toggleLock(team, day) {
            if (this.isLocked(team, day)) {
                team.locked_days = team.locked_days.filter((item) => item !== day);
                this.stops.filter((stop) => stop.team_key === team.key && stop.locked && stop.pin_day === day).forEach((stop) => {
                    stop.pin_day = null;
                    stop.pin_time = null;
                    stop.locked = false;
                });
            } else {
                const plan = this.dayOf(team, day);
                if (!plan) return;
                team.locked_days = [...team.locked_days, day].sort((a, b) => a - b);
                plan.events.filter((event) => event.type === "visit").forEach((event) => {
                    const stop = this.stopOf(event.case.id);
                    if (!stop) return;
                    stop.pin_day = day;
                    stop.pin_time = event.start;
                    stop.locked = true;
                });
            }
            this.queueSave();
        },

        visitActions(team, caseId) {
            const stop = this.stopOf(caseId);
            const locked = stop && stop.locked;
            const others = this.teams.filter((item) => item.key !== team.key);
            return [
                { text: "Dia e horário combinados…", icon: "mdi-calendar-clock", disabled: locked, handler: () => this.openPin(caseId) },
                { text: "Liberar combinado", icon: "mdi-calendar-remove", hidden: !stop || locked || (!stop.pin_day && !stop.pin_time), handler: () => this.clearPin(caseId) },
                ...(others.length ? [{ header: "Passar para" }, ...others.map((other) => ({ text: other.name, icon: "mdi-account-switch-outline", handler: () => this.moveToTeam(caseId, other.key) }))] : []),
                { divider: true },
                { text: "Abrir a ficha", icon: "mdi-open-in-new", to: `/prospeccao/casos/${caseId}` },
                { text: "Tirar do roteiro", icon: "mdi-close", danger: true, disabled: locked, handler: () => this.removeCases([caseId]) },
            ];
        },

        openPin(caseId) {
            const stop = this.stopOf(caseId);
            this.pin = { open: true, case_id: caseId, day: stop ? stop.pin_day : null, time: stop ? stop.pin_time || "" : "" };
        },

        savePin() {
            const stop = this.stopOf(this.pin.case_id);
            if (stop) {
                stop.pin_day = this.pin.day || null;
                stop.pin_time = this.pin.time || null;
                stop.locked = false;
                this.queueSave();
            }
            this.pin.open = false;
        },

        clearPin(caseId) {
            const stop = this.stopOf(caseId);
            if (!stop) return;
            stop.pin_day = null;
            stop.pin_time = null;
            stop.locked = false;
            this.queueSave();
        },

        // Equipes
        teamActions(team) {
            return [
                { text: "Receber os novos casos", icon: "mdi-cursor-default-click-outline", hidden: team.key === this.activeTeamKey, handler: () => (this.activeTeamKey = team.key) },
                { text: "Reotimizar a ordem", icon: "mdi-auto-fix", disabled: !team.manual_order, handler: () => this.optimizeTeam(team) },
                { divider: true },
                { text: "Remover equipe", icon: "mdi-delete-outline", danger: true, disabled: this.teams.length === 1, handler: () => this.removeTeam(team) },
            ];
        },

        addTeam() {
            const used = this.teams.map((team) => team.color);
            const color = PALETTE.find((item) => !used.includes(item)) || PALETTE[this.teams.length % PALETTE.length];
            const team = { key: `n${Date.now()}`, id: null, name: `Equipe ${this.teams.length + 1}`, color, member_ids: [], locked_days: [], manual_order: false };
            this.teams.push(team);
            this.activeTeamKey = team.key;
            this.rebuildLists();
            this.queueSave();
        },

        async removeTeam(team) {
            const count = this.countOf(team);
            const target = this.teams.find((item) => item.key !== team.key);
            if (count) {
                const confirmed = await this.$dialog.confirm({
                    title: "Remover equipe",
                    text: `Os ${count} ${count === 1 ? "caso" : "casos"} de "${team.name}" passam para "${target.name}". Confirma?`,
                    actions: { false: "Cancelar", true: { text: "Remover", color: "error" } },
                });
                if (!confirmed) return;
            }
            this.stops.filter((stop) => stop.team_key === team.key).forEach((stop) => {
                stop.team_key = target.key;
                stop.position = null;
                this.releasePins(stop);
            });
            this.teams = this.teams.filter((item) => item.key !== team.key);
            if (this.activeTeamKey === team.key) this.activeTeamKey = target.key;
            this.rebuildLists();
            this.queueSave();
        },

        optimizeTeam(team) {
            team.manual_order = false;
            this.stops.filter((stop) => stop.team_key === team.key).forEach((stop) => {
                stop.position = null;
            });
            this.queueSave();
        },

        setColor(team, color) {
            team.color = color;
            this.queueSave();
        },

        openSplit() {
            this.split = { open: true, saving: false, count: Math.max(2, this.teams.length) };
        },

        async submitSplit() {
            this.split.saving = true;
            try {
                await this.flush();
                const result = await axios.post(`/api/admin/prospect/trips/${this.id}/split`, { teams: this.split.count });
                this.applyServer(result.data.content, { full: true });
                this.split.open = false;
                notifySuccess(this, "Casos divididos entre as equipes.");
            } catch (error) {
                notifyError(this, error);
            } finally {
                this.split.saving = false;
            }
        },
    },
};
